import { useLocation } from "react-router";

import { AxiosError } from "axios";
import { ROUTES } from "@/shared";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../api/hooks/useAxiosPrivate";
import { ALERT_STATUS } from "../../../context/alert/types";
import { capitalizeLocationFirstLetter } from "../../../shared/functions/functions";
import useResponse from "../../../shared/hooks/useResponse";
import { DownloadFileProps } from "../types";
import { deselectAllRows } from "./../functions";

export const useFileDownload = () => {
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const location = useLocation();

  const { setSelectedRows } = useTableStoreByRoute(location.pathname as ROUTES);

  const downloadCCAFile = async ({
    passUrl,
    setIsLoading
  }: DownloadFileProps) => {
    if (passUrl) {
      try {
        setIsLoading?.(true);
        const response = await getData(passUrl, {
          responseType: "blob"
        });

        window.location.href = response.headers.location;
      } catch (error) {
        const err = error as AxiosError;
        if (err) {
          const response: any = err.response;
          handleResponse(
            ALERT_STATUS.Critical,
            response?.data?.message || err.message
          );
        }
      } finally {
        setIsLoading?.(false);
      }
    }
  };

  const downloadFile = async ({
    passUrl,
    queryString,
    noHeaders,
    successMessage,
    setIsLoading,
    onCloseDialog
  }: DownloadFileProps) => {
    const resource =
      passUrl ||
      `${import.meta.env.VITE_BASE_URL}${capitalizeLocationFirstLetter(
        location.pathname
      )}/Download?Ids=${queryString}`;

    try {
      setIsLoading?.(true);

      const response = await getData(
        resource,
        !noHeaders && {
          responseType: "blob"
        }
      );

      const blob = new Blob([response.data]);

      // decode file name to accept special characters
      const fileName = decodeURIComponent(response.headers.filename);

      const link = document.createElement("a");

      link.download = fileName;
      link.href = globalThis.URL.createObjectURL(blob);

      document.body.appendChild(link);
      link.click();

      successMessage && handleResponse(ALERT_STATUS.Success, successMessage);

      document.body.removeChild(link);
      onCloseDialog?.();
    } catch (error) {
      const err = error as AxiosError;
      if (err) {
        const response: any = err.response;
        handleResponse(
          ALERT_STATUS.Critical,
          response?.data?.message || err.message
        );
      }
    } finally {
      setIsLoading?.(false);
      deselectAllRows(setSelectedRows);
    }
  };

  return { downloadFile, downloadCCAFile };
};
